'use client';

import { useEffect, useRef, type RefObject } from 'react';
import { useSpringValue, type SpringValue } from 'react-spring';
import { useRörelse } from './RörelseKontext';

type Post = {
    el: HTMLElement;
    värde: SpringValue<number>;
    aktiv: boolean;
};

const poster = new Set<Post>();
let smutsig = false;
let planerad = 0;
let lyssnar = false;

function planera() {
    if (planerad) return;
    smutsig = true;
    planerad = requestAnimationFrame(() => {
        planerad = 0;
        if (!smutsig) return;
        smutsig = false;

        // Alla läsningar först, i ett svep, och bara för scener som är armerade.
        // Läs-sedan-skriv i den ordningen är hela skillnaden mot layout-thrash.
        const aktiva = [...poster].filter((p) => p.aktiv);
        if (!aktiva.length) return;

        const fönsterhöjd = window.innerHeight || 1;
        const mätningar = aktiva.map((post) => {
            const rekt = post.el.getBoundingClientRect();
            return { post, topp: rekt.top, höjd: rekt.height };
        });

        // Sedan alla skrivningar. set() är omedelbart — scroll-bunden rörelse ska
        // följa fingret, inte fjädra efter det.
        for (const { post, topp, höjd } of mätningar) {
            const sträcka = fönsterhöjd + höjd;
            const rå = sträcka === 0 ? 0 : (fönsterhöjd - topp) / sträcka;
            post.värde.set(rå < 0 ? 0 : rå > 1 ? 1 : rå);
        }
    });
}

function börjaLyssna() {
    if (lyssnar) return;
    lyssnar = true;
    window.addEventListener('scroll', planera, { passive: true });
    window.addEventListener('resize', planera, { passive: true });
}

function slutaLyssna() {
    if (!lyssnar || poster.size) return;
    lyssnar = false;
    window.removeEventListener('scroll', planera);
    window.removeEventListener('resize', planera);
    if (planerad) {
        cancelAnimationFrame(planerad);
        planerad = 0;
    }
}

/**
 * Scenens framsteg 0 → 1 medan den passerar genom vyporten, som ett SpringValue.
 *
 * Returnerar aldrig React-state. En scroll-hanterare som anropar setState renderar om
 * hela scenen sextio gånger i sekunden; ett SpringValue skriver stilen imperativt och
 * rör inte rendern alls. Interpolera med `.to()` och skicka värdet vidare som prop.
 *
 * `useScroll()` från react-spring används medvetet inte: den ger dokument- eller
 * containerrelativ progress, och att mappa fem scener på ett enda 0→1 blir sprött
 * i samma stund som copyn ändrar längd.
 *
 * Vid mindre rörelse låses värdet på 1 — scenen ritas i sitt färdiga tillstånd.
 */
export function useScenframsteg(ref: RefObject<HTMLElement | null>, aktiv: boolean): SpringValue<number> {
    const { mindreRörelse } = useRörelse();
    const värde = useSpringValue(mindreRörelse ? 1 : 0);
    const postRef = useRef<Post | null>(null);

    useEffect(() => {
        if (mindreRörelse) {
            värde.set(1);
            return undefined;
        }
        const el = ref.current;
        if (!el) return undefined;

        const post: Post = { el, värde, aktiv: false };
        postRef.current = post;
        poster.add(post);
        börjaLyssna();
        planera();

        return () => {
            poster.delete(post);
            postRef.current = null;
            slutaLyssna();
        };
    }, [ref, värde, mindreRörelse]);

    useEffect(() => {
        if (postRef.current) {
            postRef.current.aktiv = aktiv;
            if (aktiv) planera();
        }
    }, [aktiv]);

    return värde;
}
